import React, { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import Alert from "../common/Alert";

const EMPTY_MODIFIERS = [];

const toPositiveInt = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(1, Math.trunc(parsed));
};

const toNonNegativeInt = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.trunc(parsed));
};

const buildSelectionsFromModifiers = (modifierGroups, selectedModifiers) => {
  const selections = {};
  const modifierQtyById = new Map(
    (Array.isArray(selectedModifiers) ? selectedModifiers : []).map(
      (modifier) => [
        modifier?.modifierId || modifier?.optionId,
        toPositiveInt(modifier?.qty, 1),
      ],
    ),
  );

  (Array.isArray(modifierGroups) ? modifierGroups : []).forEach((group) => {
    const options = Array.isArray(group?.options) ? group.options : [];
    options.forEach((option) => {
      const existingQty = modifierQtyById.get(option.id);
      if (existingQty) {
        selections[option.id] = existingQty;
        return;
      }

      // Do not auto-select defaults; all modifiers start unselected.
    });
  });

  return selections;
};

const validateSelections = (modifierGroups, selections) => {
  for (const group of modifierGroups) {
    const options = Array.isArray(group?.options) ? group.options : [];
    const selectedOptions = options.filter(
      (option) => selections[option.id] > 0,
    );
    const selectedCount = selectedOptions.length;
    const totalQty = selectedOptions.reduce(
      (sum, option) => sum + toPositiveInt(selections[option.id], 1),
      0,
    );

    if (group.selectionType === "SINGLE" && selectedCount > 1) {
      return `Select only one option for ${group.name}.`;
    }

    // Removed minSelect validation to make modifiers optional
    // if (group.minSelect > 0 && selectedCount < group.minSelect) {
    //   return `Select at least ${group.minSelect} option${group.minSelect === 1 ? "" : "s"} for ${group.name}.`;
    // }

    if (
      group.maxSelect !== null &&
      group.maxSelect >= 0 &&
      selectedCount > group.maxSelect
    ) {
      return `Select no more than ${group.maxSelect} option${group.maxSelect === 1 ? "" : "s"} for ${group.name}.`;
    }

    for (const option of selectedOptions) {
      const qty = toPositiveInt(selections[option.id], 1);
      if (!option.allowQuantity && qty > 1) {
        return `${option.name} in ${group.name} cannot use quantity greater than 1.`;
      }
      if (option.maxQty && qty > option.maxQty) {
        return `${option.name} in ${group.name} allows maximum quantity ${option.maxQty}.`;
      }
    }

    if (
      group.selectionType === "SINGLE" &&
      selectedOptions.length === 1 &&
      totalQty > 1 &&
      !selectedOptions[0]?.allowQuantity
    ) {
      return `${selectedOptions[0]?.name || "Selected option"} in ${group.name} cannot use quantity greater than 1.`;
    }
  }

  return "";
};

const ModifierSelector = ({
  isOpen,
  onClose,
  onConfirm,
  modifierGroups = [],
  selectedModifiers = EMPTY_MODIFIERS,
  itemName = "Item",
  itemPrice = null,
}) => {
  const resolvedGroups = useMemo(
    () =>
      (Array.isArray(modifierGroups) ? modifierGroups : []).filter(
        (group) => group?.isActive !== false,
      ),
    [modifierGroups],
  );
  const [selections, setSelections] = useState({});
  const [validationMessage, setValidationMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setSelections(
      buildSelectionsFromModifiers(resolvedGroups, selectedModifiers),
    );
    setValidationMessage("");
  }, [isOpen, resolvedGroups, selectedModifiers]);

  const handleToggle = (group, option) => {
    setSelections((prev) => {
      const next = { ...prev };
      const isSelected = toNonNegativeInt(next[option.id], 0) > 0;

      if (group.selectionType === "SINGLE") {
        (group.options || []).forEach((groupOption) => {
          delete next[groupOption.id];
        });
      }

      if (isSelected) {
        delete next[option.id];
      } else {
        next[option.id] = option.allowQuantity
          ? Math.min(option.defaultQty || 1, option.maxQty || 1)
          : 1;
      }

      return next;
    });
    setValidationMessage("");
  };

  const handleQtyChange = (option, nextQty) => {
    const normalizedQty = Math.min(
      Math.max(0, toPositiveInt(nextQty, 1)),
      option.maxQty || 1,
    );

    setSelections((prev) => {
      const next = { ...prev };
      if (normalizedQty <= 0) {
        delete next[option.id];
      } else {
        next[option.id] = normalizedQty;
      }
      return next;
    });
    setValidationMessage("");
  };

  const handleConfirm = () => {
    const message = validateSelections(resolvedGroups, selections);
    if (message) {
      setValidationMessage(message);
      return;
    }

    const optionsById = new Map();
    resolvedGroups.forEach((group) => {
      (group.options || []).forEach((option) => {
        optionsById.set(String(option.id), {
          ...option,
          groupId: group.groupId,
          groupName: group.name,
        });
      });
    });

    const modifiers = Object.entries(selections)
      .filter(([, qty]) => toNonNegativeInt(qty, 0) > 0)
      .map(([modifierId, qty]) => {
        const option = optionsById.get(String(modifierId));
        const normalizedQty = toPositiveInt(qty, 1);
        const priceDelta = Number(option?.priceDelta || 0);

        return {
          modifierId,
          optionId: modifierId,
          groupId: option?.groupId || null,
          modifierName: option?.name || "Modifier",
          optionName: option?.name || "Modifier",
          name: option?.name || "Modifier",
          qty: normalizedQty,
          priceDelta,
          price: priceDelta,
        };
      });

    onConfirm(modifiers);
  };

  const selectedCount = useMemo(
    () =>
      Object.values(selections).reduce(
        (sum, qty) => sum + (toNonNegativeInt(qty, 0) > 0 ? 1 : 0),
        0,
      ),
    [selections],
  );

  const totalPrice = useMemo(() => {
    if (!Number.isFinite(Number(itemPrice))) return null;
    const base = Number(itemPrice);
    const optionsById = new Map();
    resolvedGroups.forEach((group) => {
      (group.options || []).forEach((option) => {
        optionsById.set(option.id, option);
      });
    });

    const modifiersTotal = Object.entries(selections).reduce(
      (sum, [optionId, qty]) => {
        const normalizedQty = toNonNegativeInt(qty, 0);
        if (normalizedQty <= 0) return sum;
        const option = optionsById.get(optionId);
        const delta = Number(option?.priceDelta || 0);
        return sum + delta * normalizedQty;
      },
      0,
    );

    return base + modifiersTotal;
  }, [itemPrice, resolvedGroups, selections]);

  const selectedModifierNames = useMemo(() => {
    const names = [];
    resolvedGroups.forEach((group) => {
      (group.options || []).forEach((option) => {
        const qty = toNonNegativeInt(selections[option.id], 0);
        if (qty > 0) {
          names.push(option.name);
        }
      });
    });
    return names;
  }, [resolvedGroups, selections]);

  const selectedModifierSummary = useMemo(() => {
    const optionsById = new Map();
    resolvedGroups.forEach((group) => {
      (group.options || []).forEach((option) => {
        optionsById.set(option.id, option);
      });
    });

    return Object.entries(selections)
      .map(([optionId, qty]) => {
        const option = optionsById.get(optionId);
        const normalizedQty = toNonNegativeInt(qty, 0);
        if (!option || normalizedQty <= 0) return null;
        const priceDelta = Number(option.priceDelta || 0);
        return {
          id: optionId,
          name: option.name,
          qty: normalizedQty,
          priceDelta,
          lineTotal: priceDelta * normalizedQty,
        };
      })
      .filter(Boolean);
  }, [resolvedGroups, selections]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={null}
      confirmText="Continue"
      onConfirm={handleConfirm}
      size="lg"
    >
      <div className="space-y-3">
        <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-white via-amber-50/40 to-white px-4 py-3 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Item
              </p>
              <h3 className="mt-1 truncate text-xl font-black text-slate-900 dark:text-slate-100">
                {itemName}
              </h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedModifierNames.length > 0 ? (
                  selectedModifierNames.map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                    >
                      {name}
                    </span>
                  ))
                ) : (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    No add-ons selected
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0 rounded-xl bg-slate-900 px-3 py-2 text-right shadow-sm dark:bg-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-300 dark:text-slate-500">
                Total
              </p>
              <p className="mt-0.5 text-xl font-black text-white dark:text-slate-900">
                {Number.isFinite(totalPrice) ? `$${totalPrice.toFixed(2)}` : "-"}
              </p>
            </div>
          </div>
        </div>

        {validationMessage && <Alert tone="warning">{validationMessage}</Alert>}

        {resolvedGroups.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            No modifiers available for this item.
          </p>
        )}

        <div className="max-h-[42vh] space-y-2 overflow-y-auto pr-1">
          {resolvedGroups.map((group) => {
            return (
              <div
                key={group.groupId}
                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/70"
              >
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Add-on Group
                    </p>
                    <h4 className="truncate text-base font-bold text-slate-800 dark:text-slate-200">
                      {group.name}
                    </h4>
                    {group.selectionType === "SINGLE" && group.maxSelect === 1 && (
                      <p className="mt-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
                        Choose 1 option
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 overflow-visible px-0.5 sm:grid-cols-2 lg:grid-cols-3">
                  {(group.options || []).map((option) => {
                    const qty = toNonNegativeInt(selections[option.id], 0);
                    const isSelected = qty > 0;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleToggle(group, option)}
                        className={[
                          "relative flex min-h-[64px] w-full flex-col items-start justify-between gap-1 rounded-xl border px-3 py-2.5 text-left transition",
                          "focus:outline-none focus:ring-2 focus:ring-amber-500 hover:shadow-md hover:-translate-y-[1px]",
                          isSelected
                            ? "border-amber-400 bg-amber-100 text-amber-950 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-100 hover:bg-amber-100"
                            : "border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-100 hover:bg-white dark:hover:bg-slate-700/80",
                        ].join(" ")}
                      >
                        {isSelected && (
                          <span className="absolute right-2.5 top-2.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-white">
                            Added
                          </span>
                        )}
                        <span className="pr-10 text-sm font-bold leading-tight">
                          {option.name}
                        </span>
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                          {option.priceDelta > 0
                            ? `+$${option.priceDelta.toFixed(2)}`
                            : "$0.00"}
                        </span>
                      </button>
                    );
                  })}

                  {group.options.length === 0 && (
                    <p className="rounded-lg border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
                      No active options available for this modifier group.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </Modal>
  );
};

export default ModifierSelector;
